import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { setTime, setTimePickup } from "../../../../store/Actions/cartActions";
import { setDTime, setPTime } from "../../../../store/Actions/authActions";
import {
  buildPickupDays,
  dateKey,
  pickupHint,
  shiftDateKey,
  DAYTIME_DELIVERY_OFFSET_DAYS,
} from "../../../../utils/slotSelection";
import SlotScreen from "./SlotScreen";
import { longDate, shortDate } from "./format";

/**
 * Checkout, étape 2/3 — créneau de collecte.
 *
 * Plancher : aujourd'hui, avec une marge de 60 minutes le jour même. Aucun
 * créneau n'est présélectionné (règle de l'app mobile). Le choix est écrit
 * dans les champs Redux que lit la création de commande :
 *   cart.pickup = { date: { day: "Thursday", date: "2026-09-24" } }
 *   auth.pickupTime = "08:00-12:00"
 */
const PickupSlot = ({
  history,
  dispatch,
  collectionTimes,
  hasCart,
  hasAddress,
  savedPickup,
  savedSlot,
  editing,
  now,
}) => {
  const { t } = useTranslation();

  const days = useMemo(
    () => buildPickupDays({ collectionTimes, now: now || new Date() }),
    [collectionTimes, now]
  );

  /* En revenant de l'écran livraison, on retrouve son choix. */
  const savedKey = dateKey(savedPickup?.date?.date);
  const initialKey = days.some((day) => day.key === savedKey)
    ? savedKey
    : days[0]?.key || null;

  const [dayKey, setDayKey] = useState(initialKey);
  const [slotRaw, setSlotRaw] = useState(
    initialKey === savedKey ? savedSlot || null : null
  );

  useEffect(() => {
    if (!days.some((day) => day.key === dayKey)) {
      setDayKey(days[0]?.key || null);
    }
  }, [days, dayKey]);

  if (!hasCart || !hasAddress) {
    return <Redirect to="/home" />;
  }

  const day = days.find((item) => item.key === dayKey) || null;
  const slot = day?.slots.find((item) => item.raw === slotRaw) || null;
  const hint = pickupHint(slot);

  const hintText = {
    default: t(
      "In the evening, your delivery is faster: evening pickup → delivery from the next available slot, daytime pickup → delivery the following week."
    ),
    daytime: `${t(
      "Daytime pickup: pick a slot after 5 pm to be delivered from the next available slot, otherwise delivery from"
    )} ${
      day ? longDate(shiftDateKey(day.key, DAYTIME_DELIVERY_OFFSET_DAYS)) : ""
    }.`,
    evening: t(
      "Evening pickup: delivery possible from the next available slot."
    ),
  }[hint.kind];

  const summary = slot ? `${shortDate(day.key)} · ${slot.label}` : null;

  const continueToDelivery = () => {
    const changed = savedKey !== day.key || savedSlot !== slot.raw;

    dispatch(setTimePickup({ date: { day: day.day, date: day.key } }));
    dispatch(setPTime(slot.raw));

    /* Une autre collecte change le plancher de livraison : on repart à zéro. */
    if (changed) {
      dispatch(setTime(null));
      dispatch(setDTime(null));
    }

    history.push("/checkout/delivery");
  };

  return (
    <SlotScreen
      t={t}
      step={{ done: 2, total: 3, label: `${t("Step")} 2 / 3` }}
      onBack={() => history.push(editing ? "/checkout" : "/home")}
      title={t("Pick a slot")}
      lede={t("When should we come and collect?")}
      days={days}
      dayKey={dayKey}
      onSelectDay={(key) => {
        setDayKey(key);
        setSlotRaw(null);
      }}
      slotRaw={slotRaw}
      onSelectSlot={(picked) => setSlotRaw(picked.raw)}
      slotsLabel={t("Pickup slots")}
      infos={[{ icon: "bulb", text: hintText }]}
      pickupSummary={summary}
      deliverySummary={null}
      ctaLabel={
        slot
          ? `${t("Continue")} · ${shortDate(day.key).split(" ")[0]} ${slot.label}`
          : t("Continue")
      }
      ctaDisabled={!slot}
      onContinue={continueToDelivery}
    />
  );
};

/* Exporté pour les tests, qui fournissent `now`. */
export { PickupSlot };

const mapStateToProps = (state) => ({
  collectionTimes: state?.auth?.selectedAddress?.[0]?.collection_times || [],
  hasAddress: Boolean(state?.auth?.selectedAddress?.[0]),
  hasCart: (state?.cart?.cartItems || []).length > 0,
  savedPickup: state?.cart?.pickup,
  savedSlot: state?.auth?.pickupTime,
  /* En modification de commande, on revient au récapitulatif d'édition. */
  editing: Boolean(state?.auth?.editContext),
});

export default connect(mapStateToProps)(PickupSlot);
